/* Admin sticker-orders dashboard.
   - Filters by status (paid / shipped / pending_payment).
   - Mark Shipped action with optional tracking number — triggers email. */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Loader2, Package, Truck, MapPin, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = ["paid", "shipped", "pending_payment", "all"];

export default function AdminStickerOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("paid");
  const [tracking, setTracking] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API}/admin/stickers/orders`
        : `${API}/admin/stickers/orders?status=${filter}`;
      const r = await axios.get(url, { withCredentials: true });
      setOrders(r.data.orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markShipped = async (order_id) => {
    setBusy(order_id);
    try {
      await axios.post(`${API}/admin/stickers/orders/${order_id}/mark-shipped`, {
        tracking_number: tracking[order_id] || null,
      }, { withCredentials: true });
      toast.success("Order marked shipped. Customer notified.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark shipped");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6" data-testid="admin-sticker-orders-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 font-body">Admin</p>
            <h1 className="font-heading text-3xl font-black tracking-tighter">Sticker Orders</h1>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-sm font-body" data-testid="admin-orders-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        <div className="flex gap-1 mb-3 border-b border-gray-200">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs font-body uppercase tracking-widest font-bold px-3 py-2 border-b-2 ${filter === s ? "border-black text-black" : "border-transparent text-gray-500"}`}
              data-testid={`filter-${s}`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading orders…
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-sm p-8 text-center text-gray-500 font-body">
            No orders in this state.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-orders-list">
            {orders.map((o) => (
              <li key={o.order_id} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`order-${o.order_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Package className="w-3.5 h-3.5 text-gray-500" />
                      <span className="text-sm font-bold text-gray-900 font-body truncate">{o.recipient_name}</span>
                      <Badge className={`text-[9px] rounded-sm px-1.5 py-0 ${
                        o.status === "paid" ? "bg-emerald-100 text-emerald-800" :
                        o.status === "shipped" ? "bg-blue-100 text-blue-800" :
                        o.status === "pending_payment" ? "bg-gray-100 text-gray-600" :
                        "bg-yellow-100 text-yellow-800"
                      } hover:bg-inherit`}>{o.status.replace("_", " ").toUpperCase()}</Badge>
                    </div>
                    <p className="text-xs text-gray-600 font-body flex items-center gap-1 mb-0.5">
                      <MapPin className="w-3 h-3 shrink-0" />
                      <span className="truncate">{o.address_line}, {o.city}, {o.state}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 font-body">
                      QR token: <code className="bg-gray-100 px-1.5 py-0.5 rounded-sm">{o.qr_token}</code>
                      &nbsp;·&nbsp; Phone: {o.phone}
                      &nbsp;·&nbsp; ₦{(o.amount_ngn || 0).toLocaleString()}
                    </p>
                    {o.notes && <p className="text-[11px] text-gray-600 italic font-body mt-0.5">Note: {o.notes}</p>}
                    {o.tracking_number && (
                      <p className="text-[11px] text-blue-700 font-body mt-0.5">
                        Tracking: <code className="bg-blue-50 px-1.5 py-0.5 rounded-sm">{o.tracking_number}</code>
                      </p>
                    )}
                  </div>
                  {o.status === "paid" && (
                    <div className="flex flex-col gap-1 shrink-0 w-44">
                      <Input
                        placeholder="Tracking # (optional)"
                        value={tracking[o.order_id] || ""}
                        onChange={(e) => setTracking((p) => ({ ...p, [o.order_id]: e.target.value }))}
                        className="h-7 text-xs rounded-sm"
                        data-testid={`tracking-${o.order_id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => markShipped(o.order_id)}
                        disabled={busy === o.order_id}
                        className="h-7 bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs"
                        data-testid={`mark-shipped-${o.order_id}`}
                      >
                        {busy === o.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Truck className="w-3 h-3 mr-1" /> Mark shipped</>}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
